import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

type LocationState = { from?: string };

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function LoginForm() {
  const { authError, clearAuthError, config, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as LocationState | null)?.from ?? "/quizzes";

  useEffect(() => {
    if (authError) setLocalError(authError);
  }, [authError]);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    redirect_uri: "postmessage",
    scope: "openid email profile",
    prompt: "select_account",
    onSuccess: async (response) => {
      try {
        setSubmitting(true);
        setLocalError(null);
        clearAuthError();
        await login(response.code);
        navigate(from, { replace: true });
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : "Login failed");
      } finally {
        setSubmitting(false);
      }
    },
    onError: () => {
      setLocalError("Google login failed. Please try again.");
    },
  } as Parameters<typeof useGoogleLogin>[0]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-mars-black px-4">
      <div className="w-full max-w-sm rounded-2xl border border-mars-line bg-mars-charcoal/60 p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/images/mars-men-logo-white.png" alt="Mars Men" className="h-6 w-auto" />
          <span className="heading text-[0.65rem] text-mars-orange">Quiz Admin</span>
        </div>
        <button
          type="button"
          onClick={() => googleLogin()}
          disabled={submitting}
          className="mono flex w-full items-center justify-center gap-3 rounded-xl border border-mars-line bg-mars-black px-4 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-mars-white transition-colors hover:border-mars-orange disabled:cursor-not-allowed disabled:opacity-60"
        >
          <GoogleIcon />
          {submitting ? "Signing in..." : "Sign in with Google"}
        </button>
        {localError ? (
          <p
            className="mono mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[0.7rem] uppercase tracking-[0.12em] text-red-400"
            role="alert"
          >
            {localError}
          </p>
        ) : null}
        <p className="mono mt-6 text-center text-[0.6rem] uppercase tracking-[0.18em] text-mars-white/40">
          Authorized {config?.allowed_email_domain || "Mars Men"} users only
        </p>
      </div>
    </div>
  );
}

export function Login() {
  const { user, config, isLoading } = useAuth();

  if (isLoading || !config) {
    return <div className="p-8 text-neutral-400">Loading sign-in...</div>;
  }
  if (!config.enabled || user) {
    return <Navigate to="/quizzes" replace />;
  }
  if (!config.google_client_id) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mars-black px-4">
        <div
          className="mono w-full max-w-md rounded-md border border-red-500/40 bg-red-500/10 p-6 text-[0.7rem] uppercase tracking-[0.12em] text-red-400"
          role="alert"
        >
          GOOGLE_OAUTH_CLIENT_ID is not configured on the server.
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={config.google_client_id}>
      <LoginForm />
    </GoogleOAuthProvider>
  );
}
